import { z } from 'zod';
import { AppDownloadsSetting } from './apps.js';
import { HexColour, Locale, TimeZone, Uuid } from './common.js';
/**
 * Settings framework (see docs/modules/core.md "Settings").
 *
 * Every module declares a zod schema whose every field has a `.default()`, so
 * `schema.parse({})` gives the full defaults. Stored values are merged over the defaults and the
 * result is validated again. A value of 0 or false switches a behaviour off.
 *
 * Two levels:
 *  - tenant settings: per tenant, per module (table `settings`), edited by tenant admins
 *  - platform settings: per installation, per module (table `platform_settings`), platform admins only
 */
export const Branding = z.object({
    /** Shown in the portal header, emails and apps. Empty = tenant name. */
    displayName: z.string().max(80).default(''),
    /** files.id of the logo (PNG/SVG/JPEG, max 1 MB). */
    logoFileId: Uuid.nullable().default(null),
    primaryColour: HexColour.default('#2563EB'),
    accentColour: HexColour.default('#F2A900'),
    /** Email "From" display name. Empty = displayName. The address stays noreply@cloudsign.uk until a sender domain is verified (Phase 4). */
    emailSenderName: z.string().max(80).default(''),
    /** Reply-To for tenant emails. Empty = none. */
    emailReplyTo: z.union([z.literal(''), z.email()]).default(''),
});
export const CoreTenantSettings = z.object({
    branding: Branding.default(Branding.parse({})),
    locale: z
        .object({
        defaultLanguage: Locale.default('en-GB'),
        /** Default for new sites and for tenant-wide emails. */
        timeZone: TimeZone.default('Europe/London'),
    })
        .default({ defaultLanguage: 'en-GB', timeZone: 'Europe/London' }),
    security: z
        .object({
        /** Force every portal user of this tenant to use two-factor authentication. */
        require2fa: z.boolean().default(false),
        /** Sign out after this many idle minutes. 0 = use the platform default. */
        sessionIdleMinutes: z.number().int().min(0).max(1440).default(0),
        /** Let platform support sign in as a tenant admin (Phase 4, core-phase4 s3): time-limited and audited. Off by default. */
        allowSupportAccess: z.boolean().default(false),
        /** Phase 4 password policy (spec 2.8). Days before a password must be changed. 0 = never expires. */
        passwordExpiryDays: z.number().int().min(0).max(730).default(0),
        /** Refuse a new password that matches one of the last N. 0 = no check. */
        passwordReuseCount: z.number().int().min(0).max(24).default(0),
        /** Forced re-authentication: a session for this tenant must sign in again after this many hours. 0 = platform default. */
        reauthHours: z.number().int().min(0).max(720).default(0),
        /** Email the tenant's administrators when platform support starts a support session. */
        supportNotifyAdmins: z.boolean().default(true),
    })
        .default({
        require2fa: false,
        sessionIdleMinutes: 0,
        allowSupportAccess: false,
        passwordExpiryDays: 0,
        passwordReuseCount: 0,
        reauthHours: 0,
        supportNotifyAdmins: true,
    }),
    email: z
        .object({
        /** Keep the outgoing email log this many days. 0 = keep forever. */
        logRetentionDays: z.number().int().min(0).max(3650).default(90),
    })
        .default({ logRetentionDays: 90 }),
});
export const CorePlatformSettings = z.object({
    auth: z
        .object({
        passwordMinLength: z.number().int().min(8).max(128).default(12),
        /** Failed logins before the account is locked. 0 = never lock. */
        maxFailedLogins: z.number().int().min(0).max(100).default(5),
        lockoutMinutes: z.number().int().min(1).max(1440).default(15),
        sessionIdleMinutes: z.number().int().min(5).max(1440).default(60),
        sessionMaxHours: z.number().int().min(1).max(720).default(12),
        inviteValidHours: z.number().int().min(1).max(720).default(72),
        passwordResetValidMinutes: z.number().int().min(5).max(1440).default(60),
        /** Days after which an account that belongs to no organisation, platform role or pending invite is deleted. 0 = keep (off). */
        orphanAccountDays: z.number().int().min(0).max(3650).default(90),
    })
        .default({
        passwordMinLength: 12,
        maxFailedLogins: 5,
        lockoutMinutes: 15,
        sessionIdleMinutes: 60,
        sessionMaxHours: 12,
        inviteValidHours: 72,
        passwordResetValidMinutes: 60,
        orphanAccountDays: 90,
    }),
    email: z
        .object({
        fromAddress: z.email().default('noreply@cloudsign.uk'),
        fromName: z.string().max(80).default('CloudSign'),
        /** Attempts before an email is marked failed. */
        maxAttempts: z.number().int().min(1).max(20).default(6),
    })
        .default({ fromAddress: 'noreply@cloudsign.uk', fromName: 'CloudSign', maxAttempts: 6 }),
    jobs: z
        .object({
        /** Delete finished jobs after this many days. 0 = keep forever. */
        keepDoneDays: z.number().int().min(0).max(365).default(7),
        /** Delete published outbox events after this many days. 0 = keep forever. */
        keepEventsDays: z.number().int().min(0).max(365).default(30),
    })
        .default({ keepDoneDays: 7, keepEventsDays: 30 }),
    files: z
        .object({
        /** Signed download links last this many seconds. */
        signedUrlSeconds: z.number().int().min(30).max(3600).default(300),
        maxUploadMb: z.number().int().min(1).max(100).default(10),
        /** Links inside kiosk config and member lists (the kiosk downloads and caches them). Phase 1, ADR 0010. */
        deviceSignedUrlSeconds: z.number().int().min(60).max(86400).default(3600),
    })
        .default({ signedUrlSeconds: 300, maxUploadMb: 10, deviceSignedUrlSeconds: 3600 }),
    realtime: z
        .object({
        /** Each live-update socket re-checks its session or device this often (0 = only when subscribing). ADR 0007. */
        revalidateSeconds: z.number().int().min(0).max(3600).default(60),
    })
        .default({ revalidateSeconds: 60 }),
    /** Phase 2 (task C3): scheduled tickers and local-time work, ADR 0016. */
    scheduling: z
        .object({
        /** How often module tickers run. Minimum 30. */
        tickSeconds: z.number().int().min(30).max(3600).default(60),
        /** A scheduled thing missed by more than this is skipped (and audited). Auto sign-out always runs. */
        maxLateMinutes: z.number().int().min(0).max(1440).default(360),
    })
        .default({ tickSeconds: 60, maxLateMinutes: 360 }),
    /** Public signed links, ADR 0017. 0 = no per-token limit. */
    links: z
        .object({ ratePerTokenPerMinute: z.number().int().min(0).max(10000).default(30) })
        .default({ ratePerTokenPerMinute: 30 }),
    /** Public API (ADR 0019): default requests per minute per key. 0 = no limit. */
    api: z.object({ ratePerMinute: z.number().int().min(0).max(100000).default(600) }).default({ ratePerMinute: 600 }),
    /** Android app download links shown on the portal's "Get the apps" page. */
    apps: AppDownloadsSetting,
    /**
     * Phase 4 (ADR 0028): the installation's multi-tenant Microsoft Entra app, used for portal SSO and directory sync.
     * Empty client id = Microsoft sign-in and sync are not offered.
     */
    entra: z
        .object({
        clientId: z.string().max(100).default(''),
        /** Encrypted with the server key (never returned by the settings API; write-only in the portal). */
        clientSecretEnc: z.string().max(4000).default(''),
        /** Refuse a Microsoft token that does not carry xms_edov=true, unless the user is already bound by object id (SEC1). */
        requireVerifiedEmailClaim: z.boolean().default(true),
    })
        .default({ clientId: '', clientSecretEnc: '', requireVerifiedEmailClaim: true }),
    support: z
        .object({
        /** Phase 4 (core-phase4 s3): "sign in as tenant admin". Longest support session in minutes. 0 = support access off everywhere. */
        maxMinutes: z.number().int().min(0).max(240).default(60),
        /** Where the portal's account-menu "Support" link sends email. Empty = no Support link. */
        email: z.union([z.literal(''), z.email().max(254)]).default('support@cloudsign.uk'),
    })
        .default({ maxMinutes: 60, email: 'support@cloudsign.uk' }),
    /** Phase 4 (ADR 0030): custom domains for tenants. 0 domains per tenant = feature off. */
    domains: z
        .object({
        /** Tenants get <slug>.<baseDomain> without DNS work. Empty = no free subdomains. */
        baseDomain: z.string().max(100).default('tenant.cloudsign.uk'),
        /** What a customer's own domain must point at (CNAME). */
        cnameTarget: z.string().max(100).default('app.cloudsign.uk'),
        maxPerTenant: z.number().int().min(0).max(10).default(2),
    })
        .default({ baseDomain: 'tenant.cloudsign.uk', cnameTarget: 'app.cloudsign.uk', maxPerTenant: 2 }),
    /** Phase 4 (core-phase4 s5): tenant closure. Days between the closure request and real deletion; export link life. */
    tenantClosure: z
        .object({
        noticeDays: z.number().int().min(7).max(365).default(30),
        exportLinkDays: z.number().int().min(1).max(30).default(7),
    })
        .default({ noticeDays: 30, exportLinkDays: 7 }),
});
/** GET /api/v1/settings/:moduleKey and GET /api/v1/platform/settings/:moduleKey */
export const SettingsResponse = z.object({
    moduleKey: z.string(),
    /** Effective values (stored merged over defaults). */
    values: z.record(z.string(), z.unknown()),
    defaults: z.record(z.string(), z.unknown()),
    /** JSON Schema of the module's settings (from z.toJSONSchema) so the portal can render a generic form. */
    jsonSchema: z.record(z.string(), z.unknown()),
    updatedAt: z.string().nullable(),
});
/** PATCH body: a deep-partial object. The server merges it, then validates the full result with the module schema. */
export const SettingsPatch = z.record(z.string(), z.unknown());
//# sourceMappingURL=settings.js.map